/**
 * Composable for managing post-lesson prompt functionality with enhanced review system
 */

import { ref, computed } from "vue";
import { getCurrentUser } from "@/services/auth.service";
import { submitReview, getLessonReview } from "@/services/lessons.service";
import { FeatureFlags } from "@/config/features";

const showPrompt = ref(false);
const currentLessonData = ref(null);
const reviewMode = ref("feedback"); // 'feedback' or 'review'
const existingReview = ref(null);
const submissionLoading = ref(false);
const submissionError = ref("");

export function usePostLessonPrompt() {
  // Computed properties
  const isReviewModeEnabled = computed(() => {
    return FeatureFlags.core.lessonRating;
  });

  const currentUser = computed(() => getCurrentUser());

  const triggerPostLessonPrompt = async (lessonData) => {
    currentLessonData.value = lessonData;
    existingReview.value = null;
    submissionError.value = "";

    // Determine which mode to show
    if (isReviewModeEnabled.value && lessonData.status === "completed") {
      reviewMode.value = "review";

      // Check if user has already submitted a review for this lesson
      if (currentUser.value) {
        try {
          const review = await getLessonReview(
            lessonData.id,
            currentUser.value.uid
          );
          if (review) {
            existingReview.value = review;
          }
        } catch (error) {
          console.warn("Could not check for existing review:", error);
        }
      }
    } else {
      reviewMode.value = "feedback";
    }

    showPrompt.value = true;
  };

  const hidePostLessonPrompt = () => {
    showPrompt.value = false;
    currentLessonData.value = null;
    existingReview.value = null;
    reviewMode.value = "feedback";
    submissionError.value = "";
  };

  const shouldShowPrompt = (lesson) => {
    // Check if lesson is completed and we haven't shown prompt yet
    if (!lesson || lesson.status !== "completed") return false;

    // For review mode, check if review has already been submitted
    if (isReviewModeEnabled.value) {
      // If lesson has a review, don't show prompt unless it's for editing
      return !lesson.hasReview;
    }

    // For legacy feedback mode, check localStorage
    const promptShownKey = `post_lesson_prompt_shown_${lesson.id}`;
    return !localStorage.getItem(promptShownKey);
  };

  const shouldShowReviewPrompt = (lesson) => {
    if (!isReviewModeEnabled.value) return false;
    if (!lesson || lesson.status !== "completed") return false;
    if (!currentUser.value) return false;

    // Only show for students who were part of the lesson
    const isStudent = lesson.studentId === currentUser.value.uid;
    const isGroupStudent =
      lesson.type === "group" &&
      lesson.currentStudents?.includes(currentUser.value.uid);

    return (isStudent || isGroupStudent) && !lesson.hasReview;
  };

  const markPromptAsShown = (lessonId) => {
    const promptShownKey = `post_lesson_prompt_shown_${lessonId}`;
    localStorage.setItem(promptShownKey, "true");
  };

  const handleFeedbackSubmitted = (feedbackData) => {
    // Mark prompt as shown for this lesson (legacy feedback)
    markPromptAsShown(feedbackData.lessonId);
    hidePostLessonPrompt();
  };

  const handleReviewSubmitted = async (reviewData) => {
    if (!currentLessonData.value || !currentUser.value) {
      submissionError.value = "Missing lesson or user data";
      return false;
    }

    try {
      submissionLoading.value = true;
      submissionError.value = "";

      // Prepare review data with user context
      const enrichedReviewData = {
        ...reviewData,
        studentId: currentUser.value.uid,
        lessonId: currentLessonData.value.id,
        teacherId: currentLessonData.value.teacherId,
      };

      // Submit the review
      const reviewId = await submitReview(enrichedReviewData);

      // Update local lesson data to reflect the review
      if (currentLessonData.value) {
        currentLessonData.value.hasReview = true;
        currentLessonData.value.reviewId = reviewId;
        currentLessonData.value.reviewSubmittedAt = new Date();
      }

      hidePostLessonPrompt();
      return true;
    } catch (error) {
      console.error("Error submitting review:", error);
      submissionError.value =
        error.message || "Failed to submit review. Please try again.";
      return false;
    } finally {
      submissionLoading.value = false;
    }
  };

  const checkForExistingReview = async (lessonId, studentId) => {
    if (!studentId) return null;

    try {
      const review = await getLessonReview(lessonId, studentId);
      return review;
    } catch (error) {
      console.warn("Could not check for existing review:", error);
      return null;
    }
  };

  const refreshReviewData = async () => {
    if (!currentLessonData.value || !currentUser.value) return;

    try {
      const review = await getLessonReview(
        currentLessonData.value.id,
        currentUser.value.uid
      );
      existingReview.value = review;
    } catch (error) {
      console.warn("Could not refresh review data:", error);
    }
  };

  return {
    // State
    showPrompt,
    currentLessonData,
    reviewMode,
    existingReview,
    submissionLoading,
    submissionError,

    // Computed
    isReviewModeEnabled,
    currentUser,

    // Methods
    triggerPostLessonPrompt,
    hidePostLessonPrompt,
    shouldShowPrompt,
    shouldShowReviewPrompt,
    markPromptAsShown,
    handleFeedbackSubmitted,
    handleReviewSubmitted,
    checkForExistingReview,
    refreshReviewData,
  };
}
